/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Popper, useTheme, Typography, Fade } from '@mui/material';
import { isArray } from 'lodash';
import React from 'react';
import {
  CustomSlider,
  sliderHeaderStyle,
  CustomIconButton,
  useScrollingInIsolation,
} from '@opengeoweb/shared';
import { useCoreTranslation } from '../../../../../utils/i18n';

interface OpacitySelectProps {
  currentOpacity: number;
  onLayerChangeOpacity: (opacity: number) => void;
  isEnabled?: boolean;
  tooltipPrefix?: string;
}

const marks = [
  {
    value: 0,
    label: '0 %',
  },
  {
    value: 0.1,
  },
  {
    value: 0.2,
  },
  {
    value: 0.3,
  },
  {
    value: 0.4,
  },
  {
    value: 0.5,
    label: '50 %',
  },
  {
    value: 0.6,
  },
  {
    value: 0.7,
  },
  {
    value: 0.8,
  },
  {
    value: 0.9,
  },
  {
    value: 1,
    label: '100 %',
  },
];

const OpacitySelect: React.FC<OpacitySelectProps> = ({
  currentOpacity,
  onLayerChangeOpacity,
  isEnabled = true,
  tooltipPrefix,
}: OpacitySelectProps) => {
  const { t } = useCoreTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const theme = useTheme();

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const closeSliderPopper = (): void => setAnchorEl(null);

  const currentOpacityText = `${Math.round(currentOpacity * 100)}  %`;
  const nonEmptyTooltipPrefix =
    tooltipPrefix ?? t('layermanager-opacity-tooltip-prefix');

  const onWheel = React.useCallback(
    (event: WheelEvent): void => {
      if (event.ctrlKey || event.metaKey) {
        const multiplier = event.altKey ? 10 : 1;
        const direction =
          event.deltaY > 0 ? 0.01 * multiplier : -0.01 * multiplier;
        const newValue = Math.min(Math.max(currentOpacity - direction, 0), 1);

        // Kind of "one frame debouncer", meant to dampen too fast slider movement
        window.requestAnimationFrame(() => {
          onLayerChangeOpacity(newValue);
        });
      }
    },
    [currentOpacity, onLayerChangeOpacity],
  );

  const onKeyDownSlider = React.useCallback(
    (event: React.KeyboardEvent): void => {
      if (event.key === 'Tab' || event.key === 'Enter') {
        event.stopPropagation();
        event.preventDefault();
        anchorEl?.focus();
        closeSliderPopper();
      }
    },
    [anchorEl],
  );

  const scrollInIsolationRef = useScrollingInIsolation<HTMLDivElement>({
    onWheel,
  });

  return (
    <Box
      data-testid="scrollOpacity"
      ref={scrollInIsolationRef}
      sx={{
        width: '100%',
        height: '32px',
        minWidth: '49px',
      }}
    >
      <CustomIconButton
        data-testid="selectOpacity"
        disableFocusRipple
        disableRipple
        onClick={handleClick}
        sx={{
          '&.MuiIconButton-root': {
            justifyContent: 'normal',
            width: '100%!important',
            height: '100%!important',
            borderRadius: '0',
            color: isEnabled
              ? 'geowebColors.layerManager.tableRowDefaultText.rgba'
              : 'geowebColors.layerManager.tableRowDisabledText.rgba',
            ...(isOpen
              ? {
                  outline: (theme): string | number =>
                    theme.palette.geowebColors.buttons.tool.focus.outline!,
                  outlineOffset: '-1px',
                  backgroundColor: 'geowebColors.buttons.tool.mouseOver.fill',
                }
              : {}),
            '&.Mui-focusVisible': {
              outlineOffset: '-1px',
            },
          },
        }}
        tooltipTitle={`${nonEmptyTooltipPrefix}${currentOpacityText}`}
      >
        <Typography
          sx={{
            fontSize: (theme): number | string =>
              theme.palette.geowebColors.layerManager.tableRowDefaultText
                .fontSize!,
          }}
          variant="subtitle2"
        >
          {currentOpacityText}
        </Typography>
      </CustomIconButton>
      <Popper
        anchorEl={anchorEl}
        open={isOpen}
        placement="bottom"
        sx={{ zIndex: 1000 }}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Box
              sx={{
                padding: '0 0 60px 0',
                backgroundColor: 'geowebColors.background.surface',
                width: '82px',
                height: '248px',
                boxSizing: 'initial',
                boxShadow: 8,
                // eslint-disable-next-line @typescript-eslint/no-misused-spread
                ...theme.palette.geowebColors.layerManager.opacitySelect.popper,
              }}
            >
              <Box sx={sliderHeaderStyle}>
                {t('layermanager-opacity-title')}
              </Box>
              <CustomSlider
                data-testid="opacitySlider"
                marks={marks}
                max={1}
                min={0}
                onBlur={(event): void => {
                  if (event.relatedTarget !== anchorEl) {
                    closeSliderPopper();
                  }
                }}
                onChange={(event, newValue: number | number[]): void => {
                  event.stopPropagation();
                  onLayerChangeOpacity(
                    isArray(newValue) ? newValue[0] : newValue,
                  );
                }}
                onKeyDown={onKeyDownSlider}
                orientation="vertical"
                shouldAutoFocus
                step={0.01}
                value={currentOpacity}
              />
            </Box>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

export default OpacitySelect;
